import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import './Auth.css';

const LogoFish = () => (
  <img src="/cloudfish-fish.png" alt="" aria-hidden="true" className="cloudfish-fish-img" />
);

const CloudFishBg = () => (
  <div className="cloudfish-bg" aria-hidden="true">
    <div className="cloudfish-cloud cloudfish-cloud--1" />
    <div className="cloudfish-cloud cloudfish-cloud--2" />
    <div className="cloudfish-cloud cloudfish-cloud--3" />
    <div className="cloudfish-cloud cloudfish-cloud--4" />
    <div className="cloudfish-cloud cloudfish-cloud--5" />
    <div className="cloudfish-fish cloudfish-fish--1"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--2"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--3"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--4"><LogoFish /></div>
    <div className="cloudfish-fish cloudfish-fish--5"><LogoFish /></div>
  </div>
);

const Login = ({ onLogin, onSwitchToSignup, signupUrl }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showResendHint, setShowResendHint] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const err = searchParams.get('saml_error') || searchParams.get('error');
    if (err) {
      toast.error(decodeURIComponent(err));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('trial_expired') === '1') {
      setLoginError('Your free trial has ended. Purchase a plan to continue using CloudFish.');
      toast.warn('Plan expired — choose a subscription to continue.', { toastId: 'trial-expired' });
      setSearchParams((prev) => {
        const p = Object.fromEntries(prev.entries());
        delete p.trial_expired;
        return p;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowResendHint(false);
    setLoginError('');

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login(email, password);
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      let msg = error.response?.data?.error || 'Login failed';
      if (!error.response) {
        msg = 'Cannot reach server. Is the backend running? Run: cd Backend && node server.js';
      }
      if (error.response?.status === 403 && error.response?.data?.code === 'FREE_TRIAL_EXPIRED') {
        setLoginError('Your free trial has ended. Purchase a plan to continue.');
        toast.error(msg);
        return;
      }
      const isInvalidCredentials = error.response?.status === 401 ||
        (msg && msg.toLowerCase().includes('invalid credentials'));
      if (isInvalidCredentials) {
        setLoginError('Incorrect username or password.');
        return;
      }
      toast.error(msg);
      if (error.response?.status === 403 && (msg.includes('verified') || error.response?.data?.email_verified === false)) {
        setShowResendHint(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Enter your email above first');
      return;
    }
    setResendLoading(true);
    try {
      const res = await authAPI.resendVerification(email);
      if (res.data.email_sent) {
        toast.success('Verification email sent! Check your inbox (and spam folder).');
        setShowResendHint(false);
      } else {
        toast.warning(res.data.email_error || res.data.message || 'Verification email could not be sent.');
      }
    } catch (error) {
      const msg = error.response?.data?.email_error || error.response?.data?.error || 'Failed to resend verification email';
      toast.error(msg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper auth-page-wrapper--login">
      <CloudFishBg />
    <div className="auth-container">
      <div className="company-info-section">
        <div className="company-logo">
          <img src="/slp-logo.png" alt="SLP Microsystems" className="logo-image logo-slp" />
          <img src="/cloudfish-logo.png" alt="CloudFish Logo" className="logo-image logo-cloudfish" />
        </div>

        <div className="product-info">
          <h3>About CloudFish</h3>
          <ul className="feature-list">
            <li>Connectivity to multiple Oracle ERP/SCM/HCM/PPM PODs</li>
            <li>Run ad-hoc queries and extract data to Excel and CSV formats</li>
            <li>Equipped with Intellisense and cloud database browser</li>
            <li>Brings productivity to your Oracle Cloud development</li>
          </ul>
        </div>

        <div className="hcm-features-section">
          <h3>Oracle HCM New Features</h3>
          <ul className="feature-list">
            <li>
              <strong>Manager Concierge &amp; Team Data Hub</strong> — Team-wide analytics, context-aware deep links, and document-support subagents (leave, compensation, employment)
            </li>
            <li>
              <strong>Time Change Request</strong> — View, search, and manage time change requests; approval history and withdraw pending requests
            </li>
            <li>
              <strong>HCM REST 26A</strong> — CloudFish supports Oracle HCM Cloud 26A REST resources; use HCM REST mode with new endpoints
            </li>
          </ul>
        </div>

        <div className="company-footer">
          <p>© 1999-{new Date().getFullYear()} SLP Microsystems, Inc. All rights reserved.</p>
        </div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <h1>Sign In</h1>
          <p>Use your SLP account email or CloudFish username</p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email or CloudFish username</label>
            <input
              type="text"
              id="email"
              autoComplete="username"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setLoginError(''); }}
              placeholder="your.email@company.com or cloudfish_username"
              required
            />
          </div>

          <div className="form-group">
            <div className="password-label-row">
              <label htmlFor="password">Password</label>
              <button 
                type="button" 
                onClick={() => navigate('/forgot-password')} 
                className="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              placeholder="••••••••"
              required
            />
          </div>

          {loginError && (
            <div className="login-error-box" role="alert" aria-live="polite">
              {loginError}
            </div>
          )}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          {showResendHint && (
            <div className="verification-hint" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f4ff', borderRadius: '6px', fontSize: '0.9rem' }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>Your email isn&apos;t verified yet. Check your inbox for the verification link, or:</p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="link-button"
                style={{ padding: '0.25rem 0', fontWeight: 600 }}
              >
                {resendLoading ? 'Sending...' : 'Resend verification email'}
              </button>
            </div>
          )}
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            {signupUrl ? (
              <a href={signupUrl} className="link-button">
                Sign up
              </a>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onSwitchToSignup?.();
                  navigate('/signup');
                }}
                className="link-button"
              >
                Sign up
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Login;

